import { Action } from "components/actions/actions";
import { BackEventDetail, BackEventType } from "components/actions/backEvent";
import { Breadcrumb } from "components/breadcrumb/breadcrumb";
import {
    Component,
    DataHolderComponent,
    EmbedableComponentDefinition,
    isNeedsUnhideComponent,
    NeedsUnhideComponent,
    ParentComponent,
} from "components/components/Component";
import { DocumentBind } from "components/events/documentBind";
import { I18nMessage } from "components/i18n/I18nMessage";
import { Renderer } from "components/Renderer";
import { StackedPage } from "components/stackedPage/stackedPage";
import $ from "jquery";
import { FocusManager } from "../focusManager";
import "./embeddedPage.scss";
import template from "./embedderPage.hbs";

export class EmbeddedPage implements ParentComponent {
    public componentType = "embeddedPage";
    private view: HTMLElement;
    public definition: EmbeddedPageDefinition;
    public id: string;
    public children: Component[] = [];
    private stack: StackItem[] = [];
    private breadcrumb: Breadcrumb;
    private lastStackAction: any;
    private replayingActionName: string;
    private closeActions: Action[] = [];;

    constructor(schema: EmbeddedPageDefinition) {
        this.definition = schema;
        this.id = this.definition.id;
    }

    public render(): Element {
        let d = document.createElement("div");
        d.innerHTML = template(this.definition);
        this.view = d as HTMLElement;

        const title = new I18nMessage(this.definition.pageTitle).toString();
        this.breadcrumb = Breadcrumb.instance(title);
        document.title = this.breadcrumb.getSislogTitle(title);

        const breadcrumbHolder = document.querySelector(".c-embedded-page__bch");
        breadcrumbHolder.innerHTML = "";
        breadcrumbHolder.prepend(this.breadcrumb.render());

        this.bindEvents();
        return this.view;
    }

    private bindEvents() {

        DocumentBind.bindEvent(this.id, "embededPageToClose", this.embededPageToCloseCallback);
        DocumentBind.bindEvent(this.id, "sislogActionEvent", this.replayReturnCallback);
        const stackHolder = this.view.querySelector(".js-stack-holder");
        stackHolder.addEventListener("pageUnstackRequested", (evt: CustomEvent<BackEventDetail>) => {
            if (evt.detail?.eventType === BackEventType.replay) {
                let action = this.stack[this.stack.length - 1].action;
                this.replayingActionName = action.definition.actionName;
                setTimeout(() => {
                    action.run(action._data, action.dataHolder);
                }, 0);
            } else if (evt.detail?.eventType === BackEventType.fireAnother) {
                for (const actionDefinition of evt.detail.response.actions) {
                    let action = new Action(actionDefinition, this);
                    this.replayingActionName = action.definition.actionName;
                    setTimeout(() => {
                        action.run(evt.detail.response.bundle.data);
                    }, 0);
                }
            } else {
                this.pop();
            }
        });

        $(stackHolder).on("pageStacked", (evt, component) => {
            this.push(component);
        });
        $(this.view).on("parentRefreshNeeded", (e) => {
            for (let index = 0; index < this.stack.length; index++) {
                const parent = this.stack[index].page.parent;
                $(parent).trigger("parentRefreshNeeded");
            }
        });

        this.view.addEventListener("stackedPageReturned", (evt: CustomEvent<BackEventDetail>) => {
            $("#js-stack-holder")[0].dispatchEvent(new CustomEvent("pageUnstackRequested", {
                bubbles: true,
                detail: evt.detail,
            }));
        });

        this.view.addEventListener("stackPageActionFired", (evt: CustomEvent) => {
            this.lastStackAction = evt.detail.action;
        });

        this.view.addEventListener("pageCloseSuscribe", (event: CustomEvent) => {
            let action = new Action(event.detail.action.action, event.detail.dataholder);
            this.closeActions.push(action);
        });
        const loader = document.querySelector(".c-embedded-page__loader");
        document.body.addEventListener("begin-loading-indicator", (e) => {
            loader.classList.add("c-embedded-page__loader--visible");
        }, false);
        document.body.addEventListener("end-loading-indicator", (e) => {
            loader.classList.remove("c-embedded-page__loader--visible");
        }, false);
    }

    private embededPageToCloseCallback = (evt: CustomEvent) => {
        if (this.closeActions != null) {
            for (const action of this.closeActions) {
                action.run(
                    (action.sourceComponent as DataHolderComponent).selectDataForAction(action.definition),
                    action.sourceComponent as DataHolderComponent);
            }
        }
    }

    /**
     * We want to delay the pop of the previous stacked page until the new stacked page is ready
     * to be shown.
     */
    private replayReturnCallback = (evt: CustomEvent) => {
        if ("finish" === evt.detail.phase
            && this.replayingActionName != null
            && evt.detail.action.definition.actionName === this.replayingActionName) {
            this.replayingActionName = null;
            this.pop();
        }
    }

    private pop() {
        let popped = this.stack.pop();
        let view = popped.parentView;
        let stackHolder = this.view.querySelector("#js-stack-holder");
        let stackHolderInner = this.view.querySelector("#js-stack-holder > .js-stack-holder__inner");
        stackHolderInner.remove();
        stackHolder.appendChild(view);
        let current = this.stack[this.stack.length - 1];
        let page: this | StackedPage | Component;
        if (current === undefined) {
            page = this;
        } else {
            page = current.page;
        }
        Renderer.iterateComponentTree(
            page,
            isNeedsUnhideComponent,
            (c) => (c as NeedsUnhideComponent).unhide());
        stackHolder.dispatchEvent(new CustomEvent("breadCrumbUnstackRequested"));
        popped.focusedItem.focus();
    }

    private push(component: StackedPage) {
        let stackHolder = this.view.querySelector("#js-stack-holder");
        let stackHolderInner = this.view.querySelector(".js-stack-holder__inner");
        this.view.querySelector(".js-stack-holder__stack").appendChild(stackHolderInner);
        this.stack.push({
            page: component,
            parentView: stackHolderInner,
            focusedItem: FocusManager.instance().getLastFocused(),
            action: this.lastStackAction,
        });
        delete this.lastStackAction;
        let newStackHolderInner = $("<div class='js-stack-holder__inner'>").appendTo(stackHolder);
        $(component.view).appendTo(newStackHolderInner);

    }

    public appendChildView = (childView: Node): void => {
        $(childView).appendTo($(this.view).find(".js-stack-holder__inner"));
    }

    public postRender() {
        this.breadcrumb.postRender();
        let focusable = this.view.querySelectorAll('button, [href], input:not([readonly]), select:not([readonly]), textarea:not([readonly]), [tabindex]:not([tabindex="-1"]), [data-s6-focusable=true]');
        if (focusable.length > 0) {
            (focusable[0] as HTMLElement).focus();
        }
    }

    public getView() {
        return this.view;
    }
}

export interface EmbeddedPageDefinition extends EmbedableComponentDefinition {
    pageTitle?: string;
}

interface StackItem {
    page: StackedPage;
    parentView: Element;
    focusedItem: HTMLElement;
    action: Action;
}
